import { Button, Chip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { setSelectedUnit } from '@/features/project/projectSlice';
import { getLongNumberStr } from '@/helpers/common';
import WorkflowsProvider, { CustomAction } from '../BuildingWorkflows/WorkflowsProvider';
import { ActionItem, FeedEvent, Process } from '@urbanmix-tech/shared-js';
import { useState } from 'react';
import { ITableRow } from '../shared/Table';
import { UnitStatusBadge } from '@/components/UnitStatus/UnitStatusBadge';
import type { ResolvedUnitStatus } from '@/helpers/unitStatus';

import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

export type DisplayProcess = {process : Process; completionEvent? : FeedEvent, lastEvent? : FeedEvent;outcome?: string; lastActionItem?: ActionItem};

type ExpiringLeaseItemProps = {
  row: ITableRow;
  processes: Process[];
  onHoverUnit?: (unitId: string | null) => void;
  onWorkflowEnd?: (unitId: string | null) => void;
  visualizerValue?: string;
  hasDraftLease?: boolean;
  /** Apartment-level status folded from the `state_*` feed, when available. */
  unitStatus?: ResolvedUnitStatus;
};

export function ExpiringLeaseItem({ row, processes,onHoverUnit,onWorkflowEnd, visualizerValue, hasDraftLease = false, unitStatus }: ExpiringLeaseItemProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { buildingId } = useParams<{ buildingId: string }>();

  const visualizer = useSelector((state: any) => state.project.projectVisualizer);
  const [hovering, setHovering] = useState(false);

  // Custom action to navigate to pricing tool with this unit
  const customActions: CustomAction[] = buildingId && row.unit?.projectId ? [
    {
      id: 'price-unit',
      name: 'Price Unit',
      onClick: () => {
        navigate(`/m/projects/${row.unit!.projectId}/${buildingId}/pricing/${row.id}`);
      },
    },
  ] : [];

  return (
    <div className="flex-1 rounded-md border border-zinc-600 px-8 py-16 mr-16"
    onMouseEnter={() => {setHovering(true);
        onHoverUnit && onHoverUnit(row.id)}}
    onMouseLeave={() => {
        setHovering(false);
        onHoverUnit && onHoverUnit(null)}}
    style={ {borderColor: visualizer.getColor(visualizerValue || row.data["Process Visual"]?.label),
            backgroundColor: hovering ? '#222222' : ''}}>
      {hasDraftLease && (
        <div className="mb-3">
          <Chip
            label="Future Lease Signed"
            size="small"
            style={{
              backgroundColor: '#4caf50',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        </div>
      )}
      <div className="flex min-h-48 flex-row items-center gap-16">
        <div>
          <Button
            variant="text"
            className="text-white"
            onClick={() =>
              dispatch(setSelectedUnit({ apartmentId: row.data.Unit.label }))
            }
          >
            <span className="text-lg">{row.data.Unit.label}</span>
          </Button>
        </div>
        <div
          className="border-1 border-1-solid flex h-32 w-32 items-center justify-center rounded-full"
          style={{
            border: 'solid 1px',
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
        >
          {row.data.Type.label === 'market' ? 'M' : 'A'}
        </div>
        <div className="text-md">{row.data.Rent.label}</div>
        <div className="flex flex-row gap-4 text-center">
          <div className="text-md">
            {row.data.Sqft.label}
            <span className="text-sm text-zinc-400"> sqft</span>
          </div>
          <div>
            {row.data['Annual Rent/Sqft'].label}
            <span className="text-sm text-zinc-400"> AR/sqft</span>
          </div>
        </div>
        <div className="flex-1">
          <div className="flex flex-col gap-2 text-right ">
            <div className="relative">
              {row.data.Days.label} <span>days </span>
              <span className="text-sm text-zinc-400">
                ({row.data['Lease Expires'].label})
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex min-h-12 flex-row items-baseline gap-12">
        <div>
                <div
                  className="flex flex-row gap-8 text-sm text-zinc-200 items-center"
                >
                    <div style={ {color: visualizer?.getColor ? visualizer.getColor(row.data["Process Visual"]?.label) : '#666'}}>
                    {row.data["Process Visual"]?.label === 'renewing' && row.data["Process Status"]?.label !== 'completed' && (
                        <AutorenewOutlinedIcon/>
                    )}
                    {row.data["Process Visual"]?.label === 'moving_out' && row.data["Process Status"]?.label !== 'completed' && (
                        <CancelOutlinedIcon/>
                    )}
                    {row.data["Process Status"]?.label === 'completed' && (
                        <CheckCircleOutlinedIcon/>
                    )}
                    </div>
                    {/*
                      Apartment-level facts (occupied/vacant, notice given,
                      renewed, incoming tenant) — the same badge the lease views
                      use, so the visual language matches across the app.
                    */}
                    {unitStatus && <UnitStatusBadge status={unitStatus} />}

                    {/*
                      Workflow bucket. Only shown when the badge doesn't already
                      say it: with `state_*` events present the badge's own
                      "Renewed"/"Notice" chips are more specific, so repeating
                      "Renewing"/"Moving Out" would just be noise. Without events
                      — which is most units today — this is the only signal there
                      is, so it must stay.
                    */}
                    {row.data["Process Visual"]?.label === 'renewing' &&
                      !unitStatus?.current?.renewal && !unitStatus?.current?.renewed && (
                      <Chip label="Renewing" size="small" style={{ backgroundColor: '#2196f3', color: 'white' }} />
                    )}
                    {row.data["Process Visual"]?.label === 'moving_out' &&
                      !unitStatus?.current?.moveOut && unitStatus?.occupancy !== 'vacant' && (
                      <Chip label="Moving Out" size="small" style={{ backgroundColor: '#f44336', color: 'white' }} />
                    )}
                    {row.data["Process Visual"]?.label === 'no_process' && (
                      <Chip label="No Process" size="small" style={{ backgroundColor: '#757575', color: 'white' }} />
                    )}
                  {row.data["Renewal Status"]?.label && row.data["Renewal Status"].label !== 'No Process' && (
                    <>
                      <div>|</div>
                      <div>{row.data["Renewal Status"].label}</div>
                    </>
                  )}
                </div>
        </div>
        <div className="flex-1  justify-items-end">
          <WorkflowsProvider
            projectId={row.unit!.projectId}
            unitId={row.id}
            variant="dropdown"
            customActions={customActions}
            onWorkflowEnd={() => {
              onWorkflowEnd && onWorkflowEnd(row.id);
            }}
          />
        </div>
      </div>
    </div>
  );
}

